/**
 * Hybrid Storage Services - Smart storage combining SQLite + localStorage
 * 
 * Usage:
 * 1. Initialize in your main App component:
 *    import { hybridManager } from './services/offline';
 *    await hybridManager.initialize();
 * 
 * 2. Use hybrid stores in your components:
 *    import { vehicleHybridStore } from './services/offline';
 *    const vehicles = await vehicleHybridStore.getAll();
 * 
 * 3. Monitor network status:
 *    import { useNetworkStatus } from './services/offline';
 *    const { isOnline } = useNetworkStatus();
 */

// Core Hybrid Services
export { hybridStorage } from './HybridStorage';
export { syncQueue } from './SyncQueue';
export { networkManager } from './NetworkManager';

// Hybrid System Manager
export { 
  hybridManager, 
  useHybridStatus, 
  useHybridInitialization, 
  useNetworkStatus,
  type HybridSystemStatus 
} from './HybridManager';

// Store Factory and Pre-configured Stores
export { 
  createHybridStore,
  vehicleHybridStore,
  driverHybridStore,
  assignmentHybridStore,
  receivingFormHybridStore,
  deliveryFormHybridStore,
  damageHybridStore,
  userPreferencesStore,
  appSettingsStore,
  tempFormDataStore,
  type HybridStoreConfig,
  type HybridStoreOperations
} from './HybridStores';

// Storage Configuration
export { 
  StorageStrategy, 
  DATA_TYPE_CONFIGS,
  type DataTypeConfig 
} from './HybridStorage';

// Quick setup guide
export const HYBRID_SETUP_GUIDE = {
  concept: "Hybrid storage: SQLite for complex data, localStorage for simple data",
  step1: "Install dependencies: npm install expo-sqlite @react-native-async-storage/async-storage",
  step2: "Initialize in App.tsx: await hybridManager.initialize()",
  step3: "Use stores: vehicleHybridStore.getAll() (auto-chooses storage)",
  step4: "Monitor status: const { isOnline } = useNetworkStatus()",
  step5: "Check sync: const { status } = useHybridStatus()",
  benefits: "Best of both worlds: Fast simple data + Complex relationships"
};